import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import OvErrorMessage from '../atoms/OvErrorMessage';
import OvStagedPlacementSummary from '../molecules/OvStagedPlacementSummary';
import OvVideoDropZone from '../molecules/OvVideoDropZone';
import OvVideoStagingRow from '../molecules/OvVideoStagingRow';
import { AdminVideo } from '../../../common/model/dto/video/admin-video';
import { ReplacementFileRejection } from '../../../common/model/type/video-file.types';
import { toPlacementValues } from '../../../common/utils/video-placement-utils';
import { useReplacementStaging } from '../../../hooks/use-replacement-staging';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvReplaceVideoFormProps {
  video: AdminVideo;
  staging: ReturnType<typeof useReplacementStaging>;
  isDisabled: boolean;
}

const OvReplaceVideoForm: FC<OvReplaceVideoFormProps> = ({
  video,
  staging,
  isDisabled,
}) => {
  const { t } = useTranslation();
  const { staged, rejection } = staging;

  const getRejectionMessage = (value: ReplacementFileRejection): string => {
    switch (value.kind) {
      case 'multiple':
        return t('videos.replace.oneFile');
      case 'file':
        return t('videos.staging.rejectedFile', {
          name: value.name,
          reason: t(`videos.staging.rejections.${value.reason}`),
        });
      default: {
        const unreachable: never = value;

        return unreachable;
      }
    }
  };

  return (
    <>
      <Notice>{t('videos.replace.stillLive')}</Notice>
      <Notice>{t('videos.replace.resetsProgress')}</Notice>

      {staged ? (
        <List aria-label={t('videos.replace.fileLabel')}>
          <OvVideoStagingRow
            item={staged}
            isDisabled={isDisabled}
            onTitleChange={(_, title) => staging.update({ title })}
            onDescriptionChange={(_, description) =>
              staging.update({ description })
            }
            onOrientationChange={(_, orientation) =>
              staging.update({ orientation })
            }
            onRemove={staging.clear}
            variant="replacement"
          />
        </List>
      ) : (
        <OvVideoDropZone
          label={t('videos.replace.chooseFile')}
          hint={t('videos.replace.dropHint')}
          limits={t('videos.replace.limits')}
          buttonLabel={t('videos.replace.chooseFile')}
          isMultiple={false}
          isDisabled={isDisabled}
          onFiles={staging.choose}
        />
      )}

      {rejection && <OvErrorMessage message={getRejectionMessage(rejection)} />}

      <Inherited>
        <span>{t('videos.details.inheritsPlacement')}</span>
        <strong>
          <OvStagedPlacementSummary values={toPlacementValues(video)} />
        </strong>
      </Inherited>
    </>
  );
};

export default OvReplaceVideoForm;

const Notice = styled.p`
  margin: 0;
  padding: ${Variables.spacing.SMALL} ${Variables.spacing.MEDIUM};
  border-left: 3px solid ${Colours.OV_ACCENT_BLUE};
  background-color: ${Colours.LIGHT_GRAY_BLUE};
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Inherited = styled.p`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0;
  color: ${Colours.OV_CHARCOAL};
`;
